import { useMemo, useState } from "react";
import type { PicRow, Rec } from "@shared/types.js";
import { fnum, fpct, fdate } from "../lib/format.js";
import { Kpi, BarRow } from "../components/Charts.js";

/** Below this many active days a per-day rate is noise, not a trend. */
const LOW_CONFIDENCE_DAYS = 5;

type SortKey = "pic" | "entered" | "crosschecked" | "perDay";

export function Pic({
  pic, rows, onPicFilter, activePic, onExport,
}: {
  pic: PicRow[];
  rows: Rec[];
  activePic: string;
  onPicFilter: (p: string) => void;
  onExport: (kind: "csv" | "copy") => void;
}) {
  /* Default to name order: this view is about how work is spread, not a ranking. */
  const [sortKey, setSortKey] = useState<SortKey>("pic");
  const [dir, setDir] = useState(1);

  const totalEntered = pic.reduce((a, p) => a + p.entered, 0);
  const totalChecked = pic.reduce((a, p) => a + p.crosschecked, 0);

  const sorted = useMemo(() => {
    const copy = pic.slice();
    copy.sort((a, b) => {
      if (sortKey === "pic") return a.pic.localeCompare(b.pic) * dir;
      return ((a[sortKey] as number) - (b[sortKey] as number)) * dir;
    });
    return copy;
  }, [pic, sortKey, dir]);

  const maxEntered = Math.max(1, ...pic.map((p) => p.entered));
  const maxChecked = Math.max(1, ...pic.map((p) => p.crosschecked));

  const entryPeople = pic.filter((p) => p.entered > 0).length;
  const reviewPeople = pic.filter((p) => p.crosschecked > 0).length;
  const reviewersOnly = pic.filter((p) => p.crosschecked > 0 && p.entered === 0);

  const unattributed = rows.filter(
    (r) => (r.nrDone && !r.nrPic) || (r.estDone && !r.estPic),
  ).length;

  const toggle = (k: SortKey) => {
    if (k === sortKey) setDir((d) => -d);
    else { setSortKey(k); setDir(k === "pic" ? 1 : -1); }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (dir === 1 ? "▲" : "▼") : "");

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 9</span>
          <h2><i className="bi bi-people hico" aria-hidden="true" />Workload distribution</h2>
          <span className="note">
            How entry and review work is spread across the team &mdash; not a performance ranking
          </span>
        </div>
        <div className="grid g-kpi">
          <Kpi icon="bi-person-plus" label="People entering rates" value={fnum(entryPeople)}
            foot="Named on at least one New Rate or Estimation" />
          <Kpi icon="bi-person-check" label="People reviewing" value={fnum(reviewPeople)}
            foot="Named on at least one crosscheck" />
          <Kpi icon="bi-pencil-square" label="Entries attributed" value={fnum(totalEntered)} tone="done"
            foot="Rows with a PIC recorded" />
          <Kpi icon="bi-shield-check" label="Crosschecks attributed" value={fnum(totalChecked)} tone="est"
            foot="Rows with a reviewer recorded" />
          <Kpi icon="bi-person-dash" label="Entries without a PIC" value={fnum(unattributed)} tone={unattributed ? "alert" : "none"}
            foot="Marked Done but nobody credited" />
        </div>

        {reviewersOnly.length > 0 && (
          <div className="callout">
            <span className="ic" />
            <div>
              <h3>
                {reviewersOnly.map((p) => p.pic).join(", ")}{" "}
                {reviewersOnly.length === 1 ? "works" : "work"} on review, not entry
              </h3>
              <p>
                {reviewersOnly.length === 1 ? "This person has" : "These people have"}{" "}
                {fnum(reviewersOnly.reduce((a, p) => a + p.crosschecked, 0))} crosschecks and no
                entries. A zero in the entry columns is a different role, not idle time &mdash;
                read the two columns separately.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="sec">
        <div className="sec-head">
          <h2><i className="bi bi-signpost-split hico" aria-hidden="true" />Two kinds of work</h2>
          <span className="note">Entry and review counted on their own scales</span>
        </div>
        <div className="grid g-2">
          <div className="card">
            <div className="card-head">
              <h3><i className="bi bi-pencil-square hico" aria-hidden="true" />Rate entry</h3>
              <span className="sub">{fnum(totalEntered)} entries &middot; New Rate vs Estimation</span>
            </div>
            <div className="bars">
              {pic.filter((p) => p.entered > 0).length === 0 && (
                <div className="empty">No attributed entries in scope.</div>
              )}
              {sorted.filter((p) => p.entered > 0).map((p) => (
                <BarRow
                  key={p.pic} label={p.pic} icon="bi-person-circle"
                  sub={`${fnum(p.activeDays)} active day${p.activeDays === 1 ? "" : "s"}`}
                  totalTxt={<><b>{fnum(p.entered)}</b> · {fpct(p.entered, totalEntered, 0)}</>}
                  segments={[
                    { label: "New Rate", value: p.newRate, color: "var(--s-done)" },
                    { label: "Estimation", value: p.estimation, color: "var(--s-est)" },
                    { label: "", value: Math.max(0, maxEntered - p.entered), color: "transparent" },
                  ]}
                />
              ))}
            </div>
            <div className="tblnote">
              <span className="pill p-done">New Rate</span>{" "}
              <span className="pill p-est">Estimation</span>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3><i className="bi bi-shield-check hico" aria-hidden="true" />Crosschecking</h3>
              <span className="sub">{fnum(totalChecked)} reviews</span>
            </div>
            <div className="bars">
              {pic.filter((p) => p.crosschecked > 0).length === 0 && (
                <div className="empty">No attributed reviews in scope.</div>
              )}
              {sorted.filter((p) => p.crosschecked > 0).map((p) => (
                <BarRow
                  key={p.pic} label={p.pic} icon="bi-person-circle"
                  sub={p.entered > 0 ? "also enters rates" : "review only"}
                  totalTxt={<><b>{fnum(p.crosschecked)}</b> · {fpct(p.crosschecked, totalChecked, 0)}</>}
                  segments={[
                    { label: "Crosschecks", value: p.crosschecked, color: "var(--focus)" },
                    { label: "", value: Math.max(0, maxChecked - p.crosschecked), color: "transparent" },
                  ]}
                />
              ))}
            </div>
            <div className="tblnote">
              Bars on both cards are scaled against the busiest person in that column, so widths
              are comparable within a card but not across the two.
            </div>
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-person-lines-fill hico" aria-hidden="true" />Per-person detail</h3>
            <div style={{ display: "flex", gap: 7, marginLeft: "auto" }}>
              {activePic && (
                <button className="btn btn--sm" type="button" onClick={() => onPicFilter("")}>
                  Clear PIC filter
                </button>
              )}
              <button className="btn btn--sm btn--primary" type="button" onClick={() => onExport("csv")}>
                Download CSV
              </button>
              <button className="btn btn--sm" type="button" onClick={() => onExport("copy")}>
                Copy for Excel
              </button>
            </div>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => toggle("pic")}>
                    PIC<span className="arw">{arrow("pic")}</span>
                  </th>
                  <th className="n">New Rate</th>
                  <th className="n">Estimation</th>
                  <th className="n sortable" onClick={() => toggle("entered")}>
                    Entries<span className="arw">{arrow("entered")}</span>
                  </th>
                  <th className="n sortable" onClick={() => toggle("crosschecked")}>
                    Crosschecks<span className="arw">{arrow("crosschecked")}</span>
                  </th>
                  <th>First entry</th>
                  <th>Last entry</th>
                  <th className="n">Active days</th>
                  <th className="n sortable" onClick={() => toggle("perDay")}>
                    Entries / day<span className="arw">{arrow("perDay")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 && (
                  <tr><td colSpan={9}><div className="empty">No attributed work in scope.</div></td></tr>
                )}
                {sorted.map((p) => {
                  const thin = p.activeDays > 0 && p.activeDays < LOW_CONFIDENCE_DAYS;
                  return (
                    <tr
                      key={p.pic} style={{ cursor: "pointer" }}
                      aria-selected={activePic === p.pic || undefined}
                      onClick={() => onPicFilter(activePic === p.pic ? "" : p.pic)}
                    >
                      <td className="strong">
                        <i className="bi bi-person-circle cell-ico" aria-hidden="true" />
                        {p.pic}
                        {p.entered === 0 && p.crosschecked > 0 && (
                          <> <span className="pill p-est">reviewer</span></>
                        )}
                        {activePic === p.pic && <> <span className="pill p-done">filtered</span></>}
                      </td>
                      <td className="n">{fnum(p.newRate)}</td>
                      <td className="n">{fnum(p.estimation)}</td>
                      <td className="n strong">{fnum(p.entered)}</td>
                      <td className="n">{fnum(p.crosschecked)}</td>
                      <td>{fdate(p.firstEntry)}</td>
                      <td>{fdate(p.lastEntry)}</td>
                      <td className="n">{fnum(p.activeDays)}</td>
                      <td className={`n${thin ? " thin" : ""}`}>
                        {p.activeDays === 0 ? (
                          <span className="muted">&ndash;</span>
                        ) : (
                          <span title={thin
                            ? `Based on only ${p.activeDays} active day(s) — too few to read as a rate`
                            : undefined}>
                            {p.perDay.toFixed(1)}
                            {thin && <span className="thinmark"> ?</span>}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="tblnote">
            <b>Active days</b> counts distinct input dates, not calendar days between first and
            last entry. Rates marked <span className="thinmark">?</span> come from fewer than{" "}
            {LOW_CONFIDENCE_DAYS} active days and are too small a sample to compare against
            someone with months of history. Rows marked Done with no input date cannot be dated
            and are excluded from both columns. Click a row to filter the whole dashboard to that
            person.
          </div>
        </div>
      </div>
    </>
  );
}
